import React from 'react';
import { makeStyles } from '@material-ui/core/styles';
import {
  Box,
  Button,
  TextField,
  InputAdornment,
  Table,
  TableHead,
  TableRow,
  TableCell,
  TableBody,
  IconButton,
  Checkbox,
  TableContainer,
  Chip,
  Collapse,
  Typography,
} from '@material-ui/core';
import AddIcon from '@material-ui/icons/Add';
import SearchIcon from '@material-ui/icons/Search';
import EditIcon from '@material-ui/icons/Edit';
import DeleteIcon from '@material-ui/icons/Delete';
import ClearIcon from '@material-ui/icons/Clear';
import KeyboardArrowDownIcon from '@material-ui/icons/KeyboardArrowDown';
import KeyboardArrowRightIcon from '@material-ui/icons/KeyboardArrowRight';
import AddFieldModal from '../../configuration/AddFieldModal';
import DeleteFieldDialog from '../../configuration/DeleteFieldDialog';
import notify from '../../../utils/notifier';
import DocuwareTemplateService from '../../../services/DocuwareTemplateService';

const useStyles = makeStyles(() => ({
  root: {
    backgroundColor: '#F9FAFB',
    borderRadius: 12,
    padding: '24px',
    height: 'calc(100vh - 130px)',
    fontFamily: '"Inter", "Helvetica Neue", Arial, sans-serif',
  },
  toolbarRow: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    marginBottom: 20,
  },
  searchField: {
    width: 320,
    backgroundColor: '#FFFFFF',
    borderRadius: 8,
    '& .MuiOutlinedInput-root': {
      borderRadius: 8,
      fontSize: 14,
      color: '#1E293B',
    },
  },
  addButton: {
    backgroundColor: '#00445D',
    color: '#FFFFFF',
    borderRadius: '24px',
    padding: '8px 20px',
    textTransform: 'none',
    fontWeight: 600,
    '&:hover': { backgroundColor: '#03374A' },
  },
  tableWrapper: {
    background: '#FFFFFF',
    borderRadius: 8,
    overflow: 'hidden',
    boxShadow: 'none',
  },
  table: {
    minWidth: 700,
    borderCollapse: 'collapse',
    '& th': {
      backgroundColor: '#F9FAFB',
      color: '#1E293B',
      fontWeight: 500,
      fontSize: 14,
      textAlign: 'left',
      borderBottom: '1px solid #E5E7EB',
      padding: '12px 16px',
    },
    '& td': {
      fontSize: 14,
      color: '#1E293B',
      borderBottom: '1px solid #E5E7EB',
      padding: '12px 16px',
      verticalAlign: 'middle',
    },
    "& tbody tr:nth-of-type(odd)": {
      backgroundColor: "#FFFFFF",
    },
    "& tbody tr:nth-of-type(even)": {
      backgroundColor: "#F8FBFF",
    },
    "& tbody tr:hover": {
      backgroundColor: "#F3F4F6",
    },
  },
  actionIcon: {
    color: '#4F4F4F',
    '&:hover': { color: '#00445D' },
  },
  checkbox: {
    color: '#00445D !important',
    '&.Mui-checked': {
      color: '#00445D !important',
    },
    '&.Mui-disabled': {
      color: '#00445D !important',
      opacity: 0.9,
    },
  },
  emptyRow: {
    color: '#9CA3AF',
    textAlign: 'center',
    padding: '24px',
  },
  fieldNameCell: {
    display: 'flex',
    alignItems: 'center',
    cursor: 'pointer',
    '&:hover': {
      color: '#00445D',
    }
  },
  expandIcon: {
    marginRight: 8,
    color: '#6B7280',
    transition: 'transform 0.2s',
  },
  extractedFieldsContainer: {
    backgroundColor: '#F8FBFF',
    padding: '12px 16px 12px 48px',
    borderBottom: '1px solid #E5E7EB',
  },
  extractedFieldsLabel: {
    fontSize: 12,
    color: '#6B7280',
    marginBottom: 8,
    fontWeight: 500,
  },
  extractedFieldChip: {
    backgroundColor: '#E9F4FA',
    color: '#00445D',
    fontSize: 12,
    height: 26,
    margin: '4px 8px 4px 0',
  },
  noExtractedFields: {
    color: '#9CA3AF',
    fontStyle: 'italic',
    fontSize: 13,
  },
  aliasCount: {
    fontSize: 12,
    color: '#6B7280',
    marginLeft: 8,
    backgroundColor: '#F0F0F0',
    padding: '2px 8px',
    borderRadius: 10,
  },
}));

export default function Fields({ currentCompany }) {
  const classes = useStyles();
  const [rows, setRows] = React.useState([]);
  const [deleteDialogOpen, setDeleteDialogOpen] = React.useState(false);
  const [selectedFieldId, setSelectedFieldId] = React.useState(null);
  const [openAddModal, setOpenAddModal] = React.useState(false);
  const [editModalOpen, setEditModalOpen] = React.useState(false);
  const [editInitialData, setEditInitialData] = React.useState(null);
  const [searchTerm, setSearchTerm] = React.useState('');
  const searchTimeout = React.useRef(null);
  
  const [expandedRows, setExpandedRows] = React.useState({});

  const organizeFields = React.useCallback((items) => {
    const canonicalFields = [];
    const aliasMap = new Map();

    items.forEach(field => {
      const parentId = field.parentFieldId || field.parent_field_id;
      if (parentId) {
        if (!aliasMap.has(parentId)) {
          aliasMap.set(parentId, []);
        }
        aliasMap.get(parentId).push({ id: field.id, name: field.name });
      } else {
        canonicalFields.push({ ...field, aliases: [] });
      }
    });

    canonicalFields.forEach(field => {
      field.aliases = aliasMap.get(field.id) || [];
    });

    return canonicalFields;
  }, []);

  const fetchList = React.useCallback(async (qText = '') => {
    try {
      const resp = await DocuwareTemplateService.getClientFields({ q: qText });
      const data = resp?.data;
      let items = [];

      if (Array.isArray(data)) items = data;
      else if (Array.isArray(data?.items)) items = data.items;
      else if (Array.isArray(data?.data)) items = data.data;
      else if (data) items = [data];

      const organized = organizeFields(items);
      setRows(organized);
    } catch (err) {
      console.error('Failed to fetch fields', err);
      notify({ type: 'error', message: 'Failed to load fields' });
    }
  }, [organizeFields]);

  React.useEffect(() => {
    fetchList('');
  }, [fetchList, currentCompany]);

  const handleDeleteField = async (id) => {
    try {
      if (DocuwareTemplateService.deleteClientField) {
        await DocuwareTemplateService.deleteClientField(id);
      }
      await fetchList('');
      notify({ type: 'success', message: 'Field deleted successfully' });
    } catch (err) {
      notify({ type: 'error', message: 'Failed to delete field' });
    } finally {
      setSelectedFieldId(null);
      setDeleteDialogOpen(false);
    }
  };

  const handleSaveField = async (payload) => {
    try {
      const fieldId = payload.id || editInitialData?.id;
      
      const body = {
        isSearchable: !!payload.searchable,
        isAutoPopulate: !!payload.autoPopulate,
        acceptedValues: Array.isArray(payload.acceptedValues)
          ? payload.acceptedValues
          : (payload.acceptedValues ? payload.acceptedValues.split(',').map(s => s.trim()) : [])
      };
      
      if (DocuwareTemplateService.updateClientField) {
        await DocuwareTemplateService.updateClientField(fieldId, body);
      }

      const existingAliases = editInitialData?.aliases || [];
      const newExtractedFields = payload.extractedFields || [];
      
      const existingNames = existingAliases.map(a => a.name);
      const toAdd = newExtractedFields.filter(name => !existingNames.includes(name));
      const toRemove = existingAliases.filter(a => !newExtractedFields.includes(a.name));

      for (const aliasName of toAdd) {
        await DocuwareTemplateService.createClientField({
          name: aliasName,
          type: editInitialData.type || 'string',
          isSearchable: false,
          isAutoPopulate: true,
          parentFieldId: fieldId
        });
      }

      for (const alias of toRemove) {
        if (alias.id) {
          await DocuwareTemplateService.deleteClientField(alias.id);
        }
      }

      await fetchList('');
      notify({ type: 'success', message: 'Field updated successfully' });
    } catch (err) {
      console.error('Error updating field', err);
      notify({ type: 'error', message: 'Failed to update field' });
    } finally {
      setEditInitialData(null);
      setEditModalOpen(false);
    }
  };

  const handleSearchChange = (e) => {
    const value = e.target.value;
    setSearchTerm(value);
    if (searchTimeout.current) clearTimeout(searchTimeout.current);
    searchTimeout.current = setTimeout(() => {
      fetchList(value);
    }, 400);
  };

  const clearSearch = () => {
    setSearchTerm('');
    fetchList('');
  };

  const toggleRowExpand = (fieldId) => {
    setExpandedRows(prev => ({
      ...prev,
      [fieldId]: !prev[fieldId]
    }));
  };

  const renderExpandedContent = (row) => {
    const aliases = row.aliases || [];
    const autoPopulateVal = row.isAutoPopulate ?? row.autoPopulate ?? false;
    
    if (!autoPopulateVal) {
      return (
        <Box className={classes.extractedFieldsContainer}>
          <Typography className={classes.noExtractedFields}>
            Auto-populate is disabled for this field
          </Typography>
        </Box>
      );
    }

    return (
      <Box className={classes.extractedFieldsContainer}>
        <Typography className={classes.extractedFieldsLabel}>
          Extracted Fields ({aliases.length})
        </Typography>
        
        <Box display="flex" flexWrap="wrap" alignItems="center">
          {aliases.length === 0 ? (
            <Typography className={classes.noExtractedFields}>
              No extracted fields configured
            </Typography>
          ) : (
            aliases.map(alias => (
              <Chip
                key={alias.id}
                label={alias.name}
                size="small"
                className={classes.extractedFieldChip}
              />
            ))
          )}
        </Box>
      </Box>
    );
  };

  return (
    <Box className={classes.root}>
      <Box className={classes.toolbarRow}>
        <TextField
          size="small"
          variant="outlined"
          placeholder="Search Field"
          value={searchTerm}
          onChange={handleSearchChange}
          className={classes.searchField}
          InputProps={{
            startAdornment: (
              <InputAdornment position="start">
                <SearchIcon style={{ color: '#6B7280' }} />
              </InputAdornment>
            ),
            endAdornment: (
              <InputAdornment position="end">
                {searchTerm && (
                  <IconButton size="small" onClick={clearSearch}>
                    <ClearIcon fontSize="small" />
                  </IconButton>
                )}
              </InputAdornment>
            ),
          }}
        />

        <Button
          className={classes.addButton}
          variant="contained"
          startIcon={<AddIcon />}
          onClick={() => setOpenAddModal(true)}
        >
          Add Field
        </Button>
      </Box>

      <div className={classes.tableWrapper}>
        <TableContainer>
          <Table className={classes.table} stickyHeader>
            <TableHead>
              <TableRow>
                <TableCell>Field Name</TableCell>
                <TableCell>Field Type</TableCell>
                <TableCell>Searchable</TableCell>
                <TableCell>Auto Populate</TableCell>
                <TableCell>Acceptable Values</TableCell>
                <TableCell align="center">Actions</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {rows.map((row) => {
                const searchableVal = row.isSearchable ?? row.searchable ?? false;
                const autoPopulateVal = row.isAutoPopulate ?? row.autoPopulate ?? false;
                const isExpanded = expandedRows[row.id] || false;
                const aliasCount = (row.aliases || []).length;
                
                return (
                  <React.Fragment key={row.id}>
                    <TableRow hover>
                      <TableCell>
                        <Box 
                          className={classes.fieldNameCell}
                          onClick={() => autoPopulateVal && toggleRowExpand(row.id)}
                        >
                          {autoPopulateVal ? (
                            isExpanded ? 
                              <KeyboardArrowDownIcon className={classes.expandIcon} /> : 
                              <KeyboardArrowRightIcon className={classes.expandIcon} />
                          ) : (
                            <Box style={{ width: 32 }} />
                          )}
                          {row.name}
                          {autoPopulateVal && aliasCount > 0 && (
                            <span className={classes.aliasCount}>{aliasCount}</span>
                          )}
                        </Box>
                      </TableCell>
                      <TableCell>{row.type}</TableCell>
                      <TableCell>
                        <Checkbox checked={!!searchableVal} disabled className={classes.checkbox} />
                      </TableCell>
                      <TableCell>
                        <Checkbox checked={!!autoPopulateVal} disabled className={classes.checkbox} />
                      </TableCell>
                      <TableCell>
                        {Array.isArray(row.acceptedValues) && row.acceptedValues.length > 0
                          ? row.acceptedValues.join(', ')
                          : '-'}
                      </TableCell>
                      <TableCell align="center">
                        <IconButton
                          size="small"
                          className={classes.actionIcon}
                          onClick={() => { setEditInitialData(row); setEditModalOpen(true); }}
                        >
                          <EditIcon fontSize="small" />
                        </IconButton>
                        <IconButton
                          size="small"
                          className={classes.actionIcon}
                          onClick={() => { setSelectedFieldId(row.id); setDeleteDialogOpen(true); }}
                        >
                          <DeleteIcon fontSize="small" />
                        </IconButton>
                      </TableCell>
                    </TableRow>
                    
                    {autoPopulateVal && (
                      <TableRow>
                        <TableCell colSpan={6} style={{ padding: 0, borderBottom: isExpanded ? '1px solid #E5E7EB' : 'none' }}>
                          <Collapse in={isExpanded} timeout="auto" unmountOnExit>
                            {renderExpandedContent(row)}
                          </Collapse>
                        </TableCell>
                      </TableRow>
                    )}
                  </React.Fragment>
                );
              })}
              {rows.length === 0 && (
                <TableRow>
                  <TableCell colSpan={6} className={classes.emptyRow}>
                    No fields found
                  </TableCell>
                </TableRow>
              )}
            </TableBody>
          </Table>
        </TableContainer>
      </div>

      <AddFieldModal
        open={openAddModal}
        onClose={() => setOpenAddModal(false)}
        onCreate={async (payload) => {
          try {
            const body = {
              name: payload.name,
              type: payload.type,
              isSearchable: !!payload.searchable,
              isAutoPopulate: !!payload.autoPopulate,
            };
            if (Array.isArray(payload.acceptedValues) && payload.acceptedValues.length > 0) {
              body.acceptedValues = payload.acceptedValues;
            }
            // Include extracted fields in the single API call
            if (payload.autoPopulate && Array.isArray(payload.extractedFields) && payload.extractedFields.length > 0) {
              body.extractedFields = payload.extractedFields;
            }
            
            await DocuwareTemplateService.createClientField(body);

            await fetchList('');
            notify({ type: 'success', message: 'Field created successfully' });
          } catch (err) {
            console.error('Error creating field', err);
            notify({ type: 'error', message: 'Failed to create field' });
          } finally {
            setOpenAddModal(false);
          }
        }}
      />

      <AddFieldModal
        open={editModalOpen}
        onClose={() => { setEditModalOpen(false); setEditInitialData(null); }}
        isEdit
        initialData={editInitialData}
        onSave={handleSaveField}
      />

      <DeleteFieldDialog
        open={deleteDialogOpen}
        onClose={() => { setDeleteDialogOpen(false); setSelectedFieldId(null); }}
        onConfirm={handleDeleteField}
        fieldId={selectedFieldId}
      />
    </Box>
  );
}
